import { saveAs } from 'file-saver';
import type { Dispatch, AnyAction } from 'redux';
import { SubmissionError } from 'redux-form';

import * as AreAutomaticRemindersEnabledAPI from 'ab-api/web/job/areAutomaticInvoiceRemindersEnabled';
import * as CreateJobAPI from 'ab-api/web/job/create';
import * as CreateInvoiceAPI from 'ab-api/web/job/createInvoice';
import * as CreateJobWorkSummaryAPI from 'ab-api/web/job/createJobWorkSummary';
import * as FindAllInProgressAPI from 'ab-api/web/job/findAllInProgress';
import * as FindAllInvoiceCodesForJobAPI from 'ab-api/web/job/findAllInvoiceCodesForJob';
import * as FindAllForInvoicesAPI from 'ab-api/web/job/findAllJobsForInvoices';
import type { W_Job_FindAllJobsForInvoices_VM } from 'ab-api/web/job/findAllJobsForInvoices/viewModel';
import * as FindAllForJobFilterAPI from 'ab-api/web/job/findAllJobsForJobFilter';
import * as FindForPreviewAPI from 'ab-api/web/job/findByIdForPreview';
import * as FindDataForWorkOrderForm from 'ab-api/web/job/findDataForWorkOrderForm';
import * as FindInvoiceByIdAPI from 'ab-api/web/job/findInvoiceById';
import * as FindInvoiceReminderRecipientsAPI from 'ab-api/web/job/findInvoiceReminderRecipients';
import * as JobInvoicesTableAPI from 'ab-api/web/job/findInvoicesTable';
import * as FindJobFormAPI from 'ab-api/web/job/findJobForm';
import * as FindForOrderAPI from 'ab-api/web/job/findJobsForOrder';
import * as FindJobsTableAPI from 'ab-api/web/job/findJobsTable';
import * as FindJobTrackingEntriesTableAPI from 'ab-api/web/job/findJobTrackingEntriesTable';
import * as FindJobTrackingSyncStatusJobIdAPI from 'ab-api/web/job/findJobTrackingSyncStatus';
import * as FindJobTrackingTableXlsxAPI from 'ab-api/web/job/findJobTrackingTableXLSX';
import * as FindJobTrackingTableXlsxStatusAPI from 'ab-api/web/job/findJobTrackingTableXLSXStatus';
import * as FindJobTrackingTemplateByJobIdAPI from 'ab-api/web/job/findJobTrackingTemplate';
import * as FindJobTrackingTemplateInformationColumnsAPI from 'ab-api/web/job/findJobTrackingTemplateInformationColumns';
import * as FindJobTrackingTotalsAPI from 'ab-api/web/job/findJobTrackingTotals';
import * as FindWorkSummaryTableAPI from 'ab-api/web/job/findJobWorkSummaryTable';
import * as FindWorkSummaryTableCSVAPI from 'ab-api/web/job/findJobWorkSummaryTableCSV';
import * as FindWorkSummaryWorkOrdersAPI from 'ab-api/web/job/findJobWorkSummaryWorkOrders';
import * as RelatedWorkOrdersForJobAPI from 'ab-api/web/job/findRelatedWorkOrdersTable';
import * as FindUserGroupAndPMForInvoicingAPI from 'ab-api/web/job/findUserGroupAndPMForInvoicing';
import * as FinishJobAPI from 'ab-api/web/job/finishJob';
import * as InvoiceJobInvoiceAPI from 'ab-api/web/job/invoiceJobInvoice';
import * as SendManualInvoiceReminderAPI from 'ab-api/web/job/sendManualInvoiceReminder';
import * as TotalRevenueAPI from 'ab-api/web/job/totalRevenue';
import * as UnfinishJobAPI from 'ab-api/web/job/unfinishJob';
import * as UpdateInvoiceAPI from 'ab-api/web/job/updateInvoice';
import * as UpdateInvoicedInvoiceAPI from 'ab-api/web/job/updateInvoicedInvoice';
import * as EditJobAPI from 'ab-api/web/job/updateJob';
import * as RestoreOriginalJWSAPI from 'ab-api/web/jobWorkSummary/restoreOriginalJobWorkSummary';
import * as UpdateJWSAPI from 'ab-api/web/jobWorkSummary/updateJobWorkSummary';

import type JobTableCSVViewModel from 'ab-viewModels/jobTableCSV.viewModel';
import type JobUpsertVM from 'ab-viewModels/workRequest/jobUpsert.viewModel';

import type * as JobUpsertRM from 'ab-requestModels/workRequest/jobUpsert.requestModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { ReportGenerationProcessStatus } from '@acceligentllc/shared/enums/reportGenerationProcessStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import type { ErrorOverride } from 'af-utils/actions.util';
import { http } from 'af-utils/http.util';
import { awaitableTimeout } from 'af-utils/timeout.util';

import * as FORMS from 'af-constants/reduxForms';
import API from 'af-constants/routes/api';

import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { errorToast, loadingToast, successToast, updateToastContent } from '@acceligentllc/storybook';

import * as scheduleBoardActionCreators from 'af-actions/scheduleBoard/scheduleBoard.actionCreators';

export function getJobForm(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindJobFormAPI.W_Job_FindJobForm_VM>(FindJobFormAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getJobsTable(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<FindJobsTableAPI.W_Job_FindJobsTable_VM>(FindJobsTableAPI.URL(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findForCSVExport(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<JobTableCSVViewModel[]>(API.V1.JOB.FIND_FOR_CSV_EXPORT(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getAllInProgress(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<FindAllInProgressAPI.W_Job_FindAllInProgress_VM>(FindAllInProgressAPI.URL(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getWorkOrderFormData(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindDataForWorkOrderForm.W_Job_FindDataForWorkOrderForm_VM>(FindDataForWorkOrderForm.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getAllJobsForOrderForm() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.get<FindForOrderAPI.W_Job_FindJobsForOrders_VM>(FindForOrderAPI.URL());
			return response.jobs;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getAllJobCodesForInvoices() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<W_Job_FindAllJobsForInvoices_VM>(FindAllForInvoicesAPI.URL());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getAllJobCodesForJobFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindAllForJobFilterAPI.W_Job_FindAllJobsForJobFilter_VM>(FindAllForJobFilterAPI.URL());

		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function finishJob(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put<void>(FinishJobAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function unfinishJob(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put<void>(UnfinishJobAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function createJob(form: CreateJobAPI.W_Job_Create_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<CreateJobAPI.W_Job_Create_VM>(CreateJobAPI.URL(), form, { submitting: FORMS.JOB_CREATE });
		};

		const error: ErrorOverride = {
			err409: () => {
				throw new SubmissionError({ jobCode: 'Job ID already taken.' });
			},
		};

		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

export function editJob(form: Partial<JobUpsertRM.Default>, jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.post<void>(EditJobAPI.URL(jobId),
				form,
				{ submitting: FORMS.JOB_EDIT }
			);
			dispatch(scheduleBoardActionCreators.CLEAR_WORK_ORDERS_BY_DATE());
			dispatch(scheduleBoardActionCreators.RELOAD_SCHEDULE_BOARD_RESOURCES());
		};

		const error: ErrorOverride = {
			err409: () => {
				throw new SubmissionError({ jobCode: 'Job ID already taken.' });
			},
		};

		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

export function copyJob(jobId: number, jobCode: string, shouldCopyAttachments: boolean) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<JobUpsertVM>(
				API.V1.JOB.COPY_JOB(jobId),
				{ jobCode, shouldCopyAttachments },
				{ submitting: FORMS.JOB_COPY }
			);
		};

		const error: ErrorOverride = {
			err409: () => {
				throw new SubmissionError({ jobCode: 'Job ID already taken.' });
			},
		};

		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

export function findByIdForPreview(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindForPreviewAPI.W_Job_JobPreview_VM>(FindForPreviewAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getWorkSummaryTable(jobId: number, tableRequestModel: TableQuery, startDate: Date, endDate: Date, subJobId: Nullable<number>) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);
			return await http.get<FindWorkSummaryTableAPI.W_Job_FindJobWorkSummaryTable_VM>(
				FindWorkSummaryTableAPI.URL(jobId, tableRequestModel, startDateString, endDateString, subJobId)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getWorkSummaryTableCSV(jobId: number, startDate: Date, endDate: Date, subJobId: Nullable<number>, filterByText: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);

			const response = await http.get<FindWorkSummaryTableCSVAPI.W_Job_FindJobWorkSummaryTableCSV_VM>(
				FindWorkSummaryTableCSVAPI.URL(jobId, startDateString, endDateString, subJobId, filterByText)
			);
			return response.rows;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getWorkOrdersForJobWorkSummary(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.get<FindWorkSummaryWorkOrdersAPI.W_Job_FindJobWorkSummaryWorkOrders_VM>(FindWorkSummaryWorkOrdersAPI.URL(jobId));
			return response.workOrders;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getWorkSummaryTotalRevenue(jobId: number, subJobId: Nullable<number>) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<TotalRevenueAPI.W_Job_TotalRevenue_VM>(TotalRevenueAPI.URL(jobId, subJobId ?? undefined));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function createJobWorkSummary(form: CreateJobWorkSummaryAPI.W_Job_CreateWorkSummary_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.post<void>(CreateJobWorkSummaryAPI.URL(), form, { submitting: FORMS.WORK_SUMMARY_BILLABLE_WORK });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editJobWorkSummary(form: UpdateJWSAPI.W_JobWorkSummary_UpdateJobWorkSummary_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.post<void>(UpdateJWSAPI.URL(), form, { submitting: FORMS.WORK_SUMMARY_BILLABLE_WORK });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function restoreOriginalJobWorkSummary(jobWorkSummaryId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.post<void>(RestoreOriginalJWSAPI.URL(jobWorkSummaryId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function createInvoice(jobId: number, form: CreateInvoiceAPI.W_Job_CreateInvoice_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<CreateInvoiceAPI.W_Job_CreateInvoice_VM>(CreateInvoiceAPI.URL(jobId), form, { submitting: FORMS.INVOICE_CREATE_FORM });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function updateInvoice(jobId: number, invoiceId: number, form: UpdateInvoiceAPI.W_Job_UpdateInvoice_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put<void>(
				UpdateInvoiceAPI.URL(jobId, invoiceId),
				form,
				{ submitting: FORMS.INVOICE_CREATE_FORM }
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function invoiceJobInvoice(jobId: number, invoiceId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.post<void>(InvoiceJobInvoiceAPI.URL(jobId, invoiceId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function updateInvoicedInvoice(jobId: number, invoiceId: number, form: UpdateInvoicedInvoiceAPI.W_Job_UpdateInvoicedInvoice_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.put<UpdateInvoicedInvoiceAPI.W_Job_UpdateInvoicedInvoice_VM>(
				UpdateInvoicedInvoiceAPI.URL(jobId, invoiceId), form, { submitting: FORMS.INVOICE_CREATE_FORM }
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function deleteInvoice(jobId: number, invoiceId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.delete(API.V1.JOB.DELETE_INVOICE(jobId, invoiceId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getInvoicesTable(
	jobId: number,
	/** format "YYYY-MM-DD" */
	startDate: string,
	/** format "YYYY-MM-DD" */
	endDate: string,
	tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<JobInvoicesTableAPI.W_Job_FindInvoiceTable_VM>(JobInvoicesTableAPI.URL(
				jobId, startDate, endDate, tableRequestModel
			));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getInvoicesTotals(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<{
				totalInvoiced: number;
				totalPaid: number;
				outstandingDebt: number;
			}>(API.V1.JOB.GET_INVOICES_TOTALS(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getInvoiceById(jobId: number, invoiceId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindInvoiceByIdAPI.W_Job_FindInvoiceById_VM>(FindInvoiceByIdAPI.URL(jobId, invoiceId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getAllInvoiceCodesForJob(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.get<FindAllInvoiceCodesForJobAPI.W_Job_FindAllInvoiceCodesForJob_VM>(FindAllInvoiceCodesForJobAPI.URL(jobId));
			return response.invoices;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function sendManualReminderForInvoice(invoiceId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<void>(SendManualInvoiceReminderAPI.URL(invoiceId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function areAutomaticRemindersEnabled() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const result = await http.get<AreAutomaticRemindersEnabledAPI.W_Job_AreAutomaticInvoiceRemindersEnabled_VM>(
				AreAutomaticRemindersEnabledAPI.URL()
			);
			return result.areAutomaticInvoiceRemindersEnabled;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getUserGroupAndPM(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindUserGroupAndPMForInvoicingAPI.W_Job_FindUserGroupAndPMForInvoicing_VM>(FindUserGroupAndPMForInvoicingAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findRelatedWorkOrders(jobId: number, startDate: Date, endDate: Date, tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const tableData = new TableQuery(tableRequestModel);
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);
			return await http.get<RelatedWorkOrdersForJobAPI.W_Job_FindRelatedWorkOrdersTable_VM>(
				RelatedWorkOrdersForJobAPI.URL(jobId, startDateString, endDateString, tableData)
			);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getJobTrackingTable(jobId: number, tableRequestModel: TableQuery, startDate: string, endDate: string, cursor: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindJobTrackingEntriesTableAPI.W_Job_JobTrackingEntries_VM>(
				FindJobTrackingEntriesTableAPI.URL(jobId, tableRequestModel, startDate, endDate, cursor)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getJobTrackingTotals(jobId: number, tableRequestModel: TableQuery, startDate: string, endDate: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindJobTrackingTotalsAPI.W_Job_JobTrackingTotals_VM>(
				FindJobTrackingTotalsAPI.URL(jobId, tableRequestModel, startDate, endDate)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function exportJobTrackingXlsx(
	jobId: number,
	data: FindJobTrackingTableXlsxAPI.W_Job_FindJobTrackingTableXLSX_RM
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const toastId = `job-tracking-${jobId}-xlsx-export`;
		const startingTimestamp = TimeUtils.formatDate(
			new Date(),
			TimeFormat.FULL_DATE_LONG
		);

		loadingToast(
			toastId,
			{
				title: 'Generating Excel export...',
				timestamp: startingTimestamp,
			}
		);

		const action = async () => {
			try {
				const generation = await http.post<
					FindJobTrackingTableXlsxAPI.W_Job_StartJobTrackingTableXLSX_VM
				>(
					FindJobTrackingTableXlsxAPI.URL(jobId),
					data
				);

				const checkStatus = async () => {
					const status = await http.get<
						FindJobTrackingTableXlsxStatusAPI.W_Job_FindJobTrackingTableXLSXStatus_VM
					>(
						FindJobTrackingTableXlsxStatusAPI.URL(
							jobId,
							generation.id
						)
					);

					switch (status.status) {
						case ReportGenerationProcessStatus.PENDING:
							updateToastContent(
								toastId,
								{
									title: 'Generating Excel export...',
									timestamp: startingTimestamp,
								}
							);
							break;

						case ReportGenerationProcessStatus.PREPARING_DATA:
							updateToastContent(
								toastId,
								{
									title: 'Preparing export data...',
									timestamp: startingTimestamp,
								}
							);
							break;

						case ReportGenerationProcessStatus.GENERATING:
							updateToastContent(
								toastId,
								{
									title: 'Generating Excel export...',
									timestamp: startingTimestamp,
								}
							);
							break;

						case ReportGenerationProcessStatus.SAVING:
							updateToastContent(
								toastId,
								{
									title: 'Saving Excel export...',
									timestamp: startingTimestamp,
								}
							);
							break;

						case ReportGenerationProcessStatus.ERROR_PREPARING_DATA:
						case ReportGenerationProcessStatus.ERROR_GENERATING:
						case ReportGenerationProcessStatus.ERROR_SAVING:
							throw new Error(
								`Job Tracking XLSX generation failed with status ${status.status}.`
							);

						case ReportGenerationProcessStatus.DONE:
							if (!status.url) {
								throw new Error(
									'Job Tracking XLSX generation finished without a download URL.'
								);
							}

							updateToastContent(
								toastId,
								{
									title: 'Downloading export...',
									timestamp: startingTimestamp,
								}
							);

							const fetchedFile = await fetch(
								status.url
							);

							const blob = await fetchedFile.blob();

							saveAs(
								blob,
								`job_tracking_${jobId}.xlsx`
							);

							successToast(
								toastId,
								{
									title: 'Export successfully downloaded',
									timestamp: TimeUtils.formatDate(
										new Date(),
										TimeFormat.FULL_DATE_LONG
									),
								}
							);

							return;

						default:
							break;
					}

					await awaitableTimeout(
						checkStatus,
						2000
					);
				};

				await checkStatus();
			} catch (err) {
				errorToast(
					toastId,
					{
						title: 'Export failed to generate',
						timestamp: TimeUtils.formatDate(
							new Date(),
							TimeFormat.FULL_DATE_LONG
						),
					}
				);

				throw err;
			}
		};

		return await errorHandler(
			action,
			dispatch,
			redirectTo
		);
	};
}

export function getInvoiceReminderRecipients(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindInvoiceReminderRecipientsAPI.W_Job_FindInvoiceReminderRecipients_VM[]>(FindInvoiceReminderRecipientsAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getJobTrackingTemplate(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindJobTrackingTemplateByJobIdAPI.W_Job_JobTrackingTemplate_VM>(FindJobTrackingTemplateByJobIdAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findJobTrackingTemplateColumns(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindJobTrackingTemplateInformationColumnsAPI.W_Job_FindJobTrackingTemplateColumns_VM>(
				FindJobTrackingTemplateInformationColumnsAPI.URL(jobId)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getJobTrackingSyncQueueStatus(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindJobTrackingSyncStatusJobIdAPI.W_Job_JobTrackingSyncStatus_VM>(FindJobTrackingSyncStatusJobIdAPI.URL(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}
